import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import type { Conversation } from '../../models/chat.model';

export interface DashboardConversationMessagePayload {
  role: string;
  content: string;
  timestamp: string;
  tokens?: number;
}

export interface DashboardConversationPayload {
  title: string;
  messages: DashboardConversationMessagePayload[];
}

export interface DashboardAggregateRequestPayload {
  uid: string;
  conversations: DashboardConversationPayload[];
}

export interface DashboardDailyAggregate {
  day: string;
  request_count: number;
  avg_iq: number;
  politics_count: number;
  business_count: number;
  social_count: number;
  consumer_shopping_count: number;
}

export interface DashboardTotalsAggregate {
  total_requests: number;
  average_iq: number;
  politics_total: number;
  business_total: number;
  social_total: number;
  consumer_shopping_total: number;
}

export interface DashboardAggregateResponse {
  uid: string;
  daily: DashboardDailyAggregate[];
  totals: DashboardTotalsAggregate;
  requestId?: string;
}

@Injectable()
export class EcommerceDashboardService {
  private readonly dashboardAggregateLambdaUrl =
    'https://bzqjjjrjcgzprndjlghiwxdjuy0mxnfu.lambda-url.us-east-1.on.aws/';

  constructor(private http: HttpClient) {}

  getDashboardAggregates(
    payload: DashboardAggregateRequestPayload
  ): Observable<DashboardAggregateResponse> {
    return this.http
      .post<any>(this.dashboardAggregateLambdaUrl, payload, {
        headers: { 'Content-Type': 'application/json' }
      })
      .pipe(map((response) => this.normalizeDashboardAggregateResponse(response)));
  }

  buildMonthlyConversationsPayload(
    conversations: Conversation[],
    nowDate: Date = new Date()
  ): DashboardConversationPayload[] {
    const cutoffMs = nowDate.getTime() - 30 * 24 * 60 * 60 * 1000;

    const out: DashboardConversationPayload[] = [];
    for (const conv of conversations || []) {
      const messages = (conv.messages || [])
        .map((message) => {
          const ts = this.toDate(message.timestamp);
          if (!ts || ts.getTime() < cutoffMs) return null;
          const content = String(message.content || '').trim();
          if (!content) return null;

          return {
            role: String(message.role || 'user'),
            content,
            timestamp: ts.toISOString(),
            ...(typeof message.tokens === 'number' ? { tokens: message.tokens } : {})
          } as DashboardConversationMessagePayload;
        })
        .filter((message): message is DashboardConversationMessagePayload => !!message);

      if (!messages.length) continue;

      out.push({
        title: String(conv.title || conv.topic || 'Conversation'),
        messages
      });
    }

    return out;
  }

  private normalizeDashboardAggregateResponse(response: any): DashboardAggregateResponse {
    if (response && typeof response === 'object' && typeof response.body === 'string') {
      try {
        const parsedBody = JSON.parse(response.body);
        if (parsedBody && typeof parsedBody === 'object') {
          return parsedBody as DashboardAggregateResponse;
        }
      } catch {
        // Fall through to raw response.
      }
    }
    return response as DashboardAggregateResponse;
  }

  private toDate(value: any): Date | null {
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
}
